import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import DOMPurify from "dompurify";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { WhatsAppFloat } from "@/components/WhatsAppFloat";
import { Badge } from "@/components/ui/badge";
import { Seo } from "@/components/Seo";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import NotFound from "@/pages/NotFound";
import { ChevronRight, Newspaper } from "lucide-react";

const looksLikeHtml = (s) => /<\/?(p|div|h[1-6]|ul|ol|li|strong|b|em|i|u|a|img|br|blockquote|span)\b/i.test(s || "");

function renderMarkdown(text) {
  return (text || "").split("\n").map((line, i) => {
    if (line.startsWith("## ")) return <h2 key={i} className="mt-8 font-display text-2xl font-bold">{line.slice(3)}</h2>;
    if (line.trim() === "") return <div key={i} className="h-3" />;
    const parts = line.split(/(\*\*[^*]+\*\*)/g).map((p, j) =>
      p.startsWith("**") && p.endsWith("**") ? <strong key={j} className="text-foreground">{p.slice(2, -2)}</strong> : p
    );
    return <p key={i} className="leading-relaxed text-muted-foreground">{parts}</p>;
  });
}

function ArticleBody({ content }) {
  if (looksLikeHtml(content)) {
    const clean = DOMPurify.sanitize(content || "", { USE_PROFILES: { html: true } });
    return <div className="tv-article-body space-y-1 leading-relaxed text-muted-foreground" dangerouslySetInnerHTML={{ __html: clean }} />;
  }
  return <div className="space-y-1">{renderMarkdown(content)}</div>;
}

export default function ArticlePage() {
  const { slug } = useParams();
  const [art, setArt] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setArt(null);
    setNotFound(false);
    api.get(`/articles/${slug}`)
      .then((r) => setArt(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return <NotFound message="Artikel tidak ditemukan." backTo="/blog" backLabel="Kembali ke Blog" />;
  if (!art) return <div className="min-h-screen bg-background"><Navbar /><div className="mx-auto max-w-3xl px-4 py-20 text-muted-foreground">Memuat...</div></div>;

  return (
    <div className="min-h-screen bg-background">
      <Seo
        type="article"
        title={art.meta_title || art.title}
        description={art.meta_description || art.excerpt || `${art.title} — artikel di Blog Travele.`}
        image={art.cover_image ? fileUrl(art.cover_image) : undefined}
        canonical={art.canonical_url}
        schema={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: art.title,
          image: art.cover_image ? fileUrl(art.cover_image) : undefined,
          author: { "@type": "Organization", name: art.author },
          publisher: { "@type": "Organization", name: "Travele" },
          datePublished: art.created_at,
          articleSection: art.category,
        }}
      />
      <Navbar />
      <Breadcrumbs
        maxWidth="max-w-3xl"
        items={[
          { name: "Beranda", path: "/" },
          { name: "Blog", path: "/blog" },
          { name: art.title },
        ]}
      />

      <article className="mx-auto max-w-3xl px-4 py-10 sm:px-6">
        <Badge className="mb-3 bg-primary/15 text-primary hover:bg-primary/15">{art.category}</Badge>
        <h1 className="font-display text-3xl font-black tracking-tight sm:text-4xl">{art.title}</h1>
        <div className="mt-2 font-mono-terminal text-xs text-muted-foreground">
          Oleh {art.author} · {new Date(art.created_at).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" })}
        </div>
        {art.cover_image && <img src={fileUrl(art.cover_image)} alt={art.title} loading="lazy" className="mt-6 aspect-[16/9] w-full rounded-md object-cover" />}
        <div className="mt-8"><ArticleBody content={art.content} /></div>

        {art.tags?.length > 0 && (
          <div className="mt-8 flex flex-wrap gap-2">
            {art.tags.map((t) => <Badge key={t} variant="secondary">#{t}</Badge>)}
          </div>
        )}

        {art.route ? (
          <div className="mt-10 border border-primary/40 bg-primary/5 p-6 text-center" data-testid="article-route-cta">
            <p className="font-display text-lg font-bold">Lihat Jadwal & Harga {art.route.label} {art.route.origin} {art.route.destination}</p>
            <p className="mt-1 text-sm text-muted-foreground">Bandingkan jadwal keberangkatan, harga, armada, dan mitra travel terbaru untuk rute ini.</p>
            <Link to={`/${art.route.slug}`}><button data-testid="article-route-btn" className="mt-3 rounded-md bg-primary px-6 py-2.5 font-bold text-primary-foreground transition-transform hover:-translate-y-0.5">Lihat Jadwal {art.route.origin} → {art.route.destination}</button></Link>
          </div>
        ) : (
          <div className="mt-10 border border-primary/40 bg-primary/5 p-6 text-center">
            <p className="font-display text-lg font-bold">Siap bepergian?</p>
            <p className="mt-1 text-sm text-muted-foreground">Cari jadwal travel door to door & charter drop off di Digital Terminal Travele.</p>
            <Link to="/"><button className="mt-3 rounded-md bg-primary px-6 py-2.5 font-bold text-primary-foreground transition-transform hover:-translate-y-0.5">Cari Travel Sekarang</button></Link>
          </div>
        )}

        {art.related?.length > 0 && (
          <>
            <h2 className="mb-4 mt-12 font-display text-2xl font-bold">Artikel Terkait</h2>
            <div className="grid gap-3 sm:grid-cols-2">
              {art.related.map((r) => (
                <Link key={r.slug} to={`/blog/${r.slug}`} className="flex items-center gap-3 border border-border bg-card p-4 transition-colors hover:border-primary">
                  <Newspaper className="h-5 w-5 shrink-0 text-primary" />
                  <span className="text-sm font-semibold">{r.title}</span>
                </Link>
              ))}
            </div>
          </>
        )}
      </article>

      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
